import React, { memo, useCallback, useEffect, useMemo } from 'react';
import classNames from 'classnames';

import { LoadingState } from '@atlas/base';
import { BonusTitle, QuickBonus, Slider } from '@atlas/components';
import { AccountManager, useSettings } from '@atlas/core';
import { Icon, Label, Loader, ToggleCheckbox } from '@atlas/ui';

import { CDN_TYPE_CRM, imageUrlCDN } from 'common/utils/utils';
import useBonusesService from 'hooks/useBonusesService';
import { BASE_PATH } from 'src/common/constants';
import { useBonusesContext } from 'src/components/BonusesProvider';
import {
  getMinDeposit,
  isAmountEmptyOrBelowMinDeposit,
} from 'src/components/BonusesProvider/utils';
import useBonusSliderAtEnd from 'src/hooks/useBonusSliderAtEnd';

const QuickDepositBonuses = () => {
  const bonusesService = useBonusesService();
  const currency = AccountManager.getAccountCurrency();
  const {
    bonusesList,
    depositAmount,
    isLockedBonuses,
    isValidatingBonus,
    paymentMethod,
    selectedBonus,
    setSelectedBonus,
  } = useBonusesContext();
  const { lang } = useSettings();
  const sliderKey = useMemo(() => bonusesList.map(({ id }) => id).join('-'), [bonusesList]);
  const {
    atEnd: bonusSliderAtEnd,
    onSwiper: onBonusSwiper,
    scrollToSlide,
  } = useBonusSliderAtEnd(sliderKey);
  const selectedBonusId = selectedBonus?.id;

  const selectedBonusIndex = useMemo(
    () => bonusesList.findIndex(bonus => bonus.id === selectedBonusId),
    [bonusesList, selectedBonusId],
  );
  const hasSingleBonus = bonusesList.length === 1;

  useEffect(() => {
    if (hasSingleBonus || !selectedBonusId || !paymentMethod?.id) {
      return;
    }

    if (selectedBonusIndex >= 0) {
      scrollToSlide(selectedBonusIndex, { force: true });
    }
  }, [hasSingleBonus, paymentMethod?.id, scrollToSlide, selectedBonusId, selectedBonusIndex]);

  const handleSelectBonus = useCallback(
    bonus => {
      const isActive = selectedBonus?.id === bonus.id;
      const newActiveBonus = isActive ? null : bonus;
      setSelectedBonus(newActiveBonus);
    },
    [selectedBonus, setSelectedBonus],
  );

  const sliderConfig = useMemo(
    () => ({
      autoHeight: false,
      autoplay: false,
      loop: false,
      nextBtn: <Slider.NextBtn>next</Slider.NextBtn>,
      prevBtn: <Slider.PrevBtn>prev</Slider.PrevBtn>,
      slidesPerView: 'auto',
      spaceBetween: '8px',
    }),
    [],
  );

  if (bonusesService?.loading === LoadingState.loading) {
    return <Loader className="profile-deposit__loader" />;
  }

  if (!bonusesList.length) {
    return null;
  }

  const renderBonus = bonus => {
    const finance = bonus?.financeDataList?.find(item => item.currency === currency);
    const freeSpinMinAmount = bonus?.freeSpinMinAmountsDeposit?.[currency];
    const image = bonus?.getImage(lang, 'ENG');

    const bonusImage = image
      ? `${imageUrlCDN(image, CDN_TYPE_CRM)}`
      : `${BASE_PATH}/static/assets/images/gift.png`;

    const isBelowMinDeposit = isAmountEmptyOrBelowMinDeposit(
      depositAmount,
      getMinDeposit(bonus, currency),
    );

    const isActive = selectedBonus?.id === +bonus.id && !isBelowMinDeposit;
    const isActiveSelection = isActive && !isValidatingBonus && !bonus?.disabled;

    const bonusTitle = (
      <h4 className="quick-deposit-bonus-title">
        <BonusTitle name={bonus?.name} nameHighlighted={bonus?.nameHighlighted} />
      </h4>
    );

    const bonusDescription = (
      <div className="quick-deposit-bonus-description">
        <Label
          message="deposit_min_amount"
          params={{
            currency: bonus?.currency,
            minBonusAmount: freeSpinMinAmount ?? finance?.minDeposit ?? 0,
          }}
        />
      </div>
    );

    const disableBlock = (
      <div className="bonus-disable quick-deposit-disable">
        <Icon className="disabled-icon" name="lock" />
        <Label message="deposit_bonus_unavaliable_message" />
      </div>
    );

    return (
      <button
        className={classNames('quick-deposit-bonus', {
          ['quick-deposit-bonus--single']: hasSingleBonus,
        })}
        key={bonus?.id}
        onClick={() => handleSelectBonus(bonus)}
        type="button"
      >
        <QuickBonus
          active={isActiveSelection}
          description={bonusDescription}
          disabled={bonus?.disabled || false}
          disabledBlock={disableBlock}
          image={bonusImage}
          title={bonusTitle}
          toggleCheckbox={
            <div className="quick-deposit-bonus-toggle">
              <ToggleCheckbox
                checked={isActiveSelection}
                disabled={
                  bonus?.disabled || isBelowMinDeposit || isLockedBonuses || isValidatingBonus
                }
                onChange={() => handleSelectBonus(bonus)}
              />
            </div>
          }
        />
      </button>
    );
  };

  if (hasSingleBonus) {
    return renderBonus(bonusesList[0]);
  }

  return (
    <div
      className={classNames('quick-deposit-wrapper', {
        single: hasSingleBonus,
      })}
    >
      {!hasSingleBonus && <Label className="slider-title" message="insufficient_funds_get_bonus" />}
      <div
        className={classNames('bonus-slider-outer', {
          'bonus-slider-outer--at-end': bonusSliderAtEnd,
        })}
      >
        <Slider key={sliderKey} {...sliderConfig} onSwiper={onBonusSwiper}>
          {bonusesList.map(bonus => (
            <Slider.Slide className="quick-deposit-slide" key={bonus?.id}>
              {renderBonus(bonus)}
            </Slider.Slide>
          ))}
        </Slider>
      </div>
    </div>
  );
};

export default memo(QuickDepositBonuses);
